import React from 'react';
import { useState, useEffect, useFormContext, Hooks, Code, Form, Field, Name, Title, Info, Actions, Toast, API, Utility, Button, Files, Control, Messages, OptionType } from 'helpers/FormImports';
import { MediaList, MediaUpload, useProfileImage, useSignatureImage } from 'helpers/ImageImports';
import { User } from 'models/UserModel';
import { SingleSelectList } from 'components/common/MultiSelectList';

const UserForm: React.FC = () => {
  const width = { view: 20, open: 20, modify: 20 };
  const [formId, formName] = [Code.Form.Id.USER, Code.Form.Name.USER];
  const {keyId, activeId, setActiveId, loading, setLoading, message, setMessage, showMenu, setShowMenu, confirm, setConfirm, error, setError, menuPosition, setMenuPosition, contextMenuRef, iframeRef, formRef } = useFormContext();
  const [activeTab, setActiveTab] = useState<string>(Utility.Storage.GetTab(formName));
  const [roleOptions, setRoleOptions] = Hooks.Form.Options();
  const [locationOptions, setLocationOptions] = Hooks.Form.Options();
  const [cityOptions, setCityOptions] = Hooks.Form.Options();
  const [genderOptions, setGenderOptions] = Hooks.Form.Options();
  const [doctorOptions, setDoctorOptions] = Hooks.Form.Options();
  const [employeeOptions, setEmployeeOptions] = Hooks.Form.Options();
  const [authOptions, setAuthOptions] = Hooks.Form.Options();
  const [formData, setFormData] = useState<User>({});
  const [fileList, setFileList] = useState<MediaList>({});
  const [selectedDoctors, setSelectedDoctors] = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [allDoctorList, setAllDoctorList] = Hooks.Form.Options();
  const [allLocationList, setAllLocationList] = Hooks.Form.Options();
  const [menuList, setMenuList] = Hooks.Form.Options();
  const [selectedMenu, setSelectedMenu] = useState<Set<string>>(new Set());
  const [menuFilter, setMenuFilter] = useState<string>('');
  
  const [profileUrl, setProfileUrl] = useState<string | null>(null);
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);
  const {profileImage, handleProfileImageChange, handleAttachProfileImage, handleDeleteProfileImage } = useProfileImage(fileList, setFileList, setProfileUrl, setError);
  const {signatureImage, handleSignatureImageChange, handleAttachSignatureImage, handleDeleteSignatureImage } = useSignatureImage(fileList, setFileList, setSignatureUrl, setError);
 
  Form.Click.Outside([contextMenuRef],[() => setShowMenu(false),() => {}],true);
  const Privilege = Hooks.Privilege.List(formId);
  Hooks.Privilege.HasOpen(Privilege);
  
  useEffect(() => {
    Files.Json.API(Code.File.ROLE, Code.Option.Common.SELECT, setRoleOptions, setError);
    Files.Json.API(Code.File.LOCATION, Code.Option.Common.SELECT, setLocationOptions, setError);
    Files.Json.API(Code.File.CITY, Code.Option.Common.SELECT, setCityOptions, setError);
    Files.Json.API(Code.File.DOCTOR, Code.Option.Common.NA, setDoctorOptions, setError);
    Files.Json.API(Code.File.EMPLOYEE, Code.Option.Common.NA, setEmployeeOptions, setError);
    Files.Json.Http(Code.File.GENDER, Code.Option.Common.NA, setGenderOptions, setError);
    Files.Json.Http(Code.File.AUTH_METHOD, Code.Option.Common.NOTHING, setAuthOptions, setError);
    Files.Json.API(Code.File.FORM, Code.Option.Common.NOTHING, setMenuList, setError,true);
    Files.Json.API(Code.File.DOCTOR, Code.Option.Common.NOTHING, setAllDoctorList, setError);
    Files.Json.API(Code.File.LOCATION, Code.Option.Common.NOTHING, setAllLocationList, setError);
  
    API.Data.GetByKeyId<User>(formName, keyId, setLoading, setError)
      .then(async(data) => { if(data) {
        setActiveId(data.user_active); 
        Files.List.ParseFile(data.file_list, setFileList);
        const formData = await Form.Map.Data(data);
        setFormData((prevData) => ({
          ...prevData,
          ...formData,
        }));
        setSelectedDoctors(JSON.parse(data.doctor_list!));
        setSelectedLocations(JSON.parse(data.location_list!));
        setSelectedMenu(Utility.Convert.SetString(data.menu_list))
      }})
      setLoading(false);
  }, []);

  const onSubmit = (e: React.FormEvent) => {
    if (!Toast.Validate.Form(e,setMessage)) return;

    const finalData = Utility.Sanitize.FormData({
      ...formData,
      company_id: Utility.Storage.CompanyId(),
      entry_id: Utility.Storage.UserId(),
      user_first_name: Utility.Convert.TitleCase(formData.user_first_name),
      user_middle_name: Utility.Convert.TitleCase(formData.user_middle_name),
      user_last_name: Utility.Convert.TitleCase(formData.user_last_name),
      user_phone: Utility.Convert.Number(formData.user_phone),
      doctor_list: JSON.stringify(selectedDoctors),
      location_list: JSON.stringify(selectedLocations),
      file_list: JSON.stringify(Files.List.Clean(fileList)),
      menu_list: JSON.stringify(Array.from(selectedMenu))
    });

    API.Data.Save(formName, keyId, finalData, setMessage)

  };

  return (
    <Control.Form.Frame setMenuPosition={setMenuPosition} setShowMenu={setShowMenu}>
      <Messages.Toast Messages={message} Error={error} />
      <Messages.Confirm Messages={confirm} onConfirm={() => API.Data.Delete(formName, keyId, activeId, setMessage)} onRefuse={()=>{}} />
      {!loading && (
        <form id={Code.Form.Name.EDIT} ref={formRef} onSubmit={onSubmit} autoComplete="off">
          <Button.Save keyId={keyId} activeId={activeId} handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)} formName={formName} formId={formId} Privilege={Privilege} />
          <Control.Tab.Header titles={["Details", "Credentials", "Images", "Menu"]} activeTab={activeTab} formName={formName} setActiveTab={setActiveTab} />
          <Control.Form.Data>
 
            <Control.Tab.Content id={Code.Tab.Tab1} isActive={activeTab === Code.Tab.Tab1}>
              <Control.Row>
                <Field.Text id={Name.User.FirstName} value={formData.user_first_name} title={Title.FirstName} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,34,2]} require={true} />
                <Field.Text id={Name.User.MiddleName} value={formData.user_middle_name} title={Title.MiddleName} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,34,2]} />
                <Field.Text id={Name.User.LastName} value={formData.user_last_name} title={Title.LastName} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,32,0]} />
              </Control.Row>
              <Control.Row>
                <Field.Text id={Name.User.Phone} value={Utility.Format.Phone(formData.user_phone)} title={Title.Phone} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} require={true} />
                <Field.Text id={Name.User.Email} value={formData.user_email} title={Title.Email} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} require={true} />
              </Control.Row>
              <Control.Row>
                <Field.Area id={Name.User.Address} value={formData.user_address} title={Title.Address} onChange={(e) => Form.Field.Change(e, formData, setFormData)} />
              </Control.Row>
              <Control.Row>
                <Field.Select id={Name.City.Id} value={formData.city_id} title={Title.City} options={cityOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)}  size={[0,50,1]} require={true} />
                <Field.Text id={Name.User.Postal} value={formData.user_postal} title={Title.Postal} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} />
              </Control.Row>
              <Control.Box>
                <Field.Select id={Name.Common.GenderId} value={formData.gender_id} title={Title.Gender} options={genderOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} />
                <Field.MultiSelect title="Assigned Doctors" id={Title.Doctor} options={allDoctorList} selectedItems={selectedDoctors} setSelectedItems={setSelectedDoctors}/>
              </Control.Box>
            </Control.Tab.Content>

            <Control.Tab.Content id={Code.Tab.Tab2} isActive={activeTab === Code.Tab.Tab2}>
              <Control.Row>
                <Field.Text id={Name.User.Login} value={formData.user_login} title={Title.Login} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} require={true} />
                <Field.Password id={Name.User.Pass} value={formData.user_pass} title={Title.Password}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} keyId={keyId} />
              </Control.Row>
              <Control.Row>
                <Field.Select id={Name.Location.Id} value={formData.location_id} title={"Default Location"} options={locationOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} require={true} />
                <Field.FilterSelect id={Name.Role.Id} value={formData.role_id} title={Title.Role} options={roleOptions} setFormData={setFormData} size={[1,50,0]}  require={true} />
              </Control.Row>
              <Control.Row>
                <Field.Select id={Name.User.MfaId} value={formData.mfa_id} title={"Authentication"} options={authOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} require={true} />
                <Field.Text id={Name.User.MfaData} value={formData.mfa_data} title={"Authentication Email"} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} require={true} />
              </Control.Row>
              <Control.Row>
                <Field.Select id={Name.Doctor.Id} value={formData.doctor_id} title='Doctor Mapping' options={doctorOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)}  size={[0,50,1]} />
                <Field.Select id={Name.Employee.Id} value={formData.employee_id} title='Employee Mapping' options={employeeOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)}  size={[1,50,0]} />
              </Control.Row>
              <Control.Box>
                <Field.MultiSelect title="Assigned Locations" id={Title.Location} options={allLocationList} selectedItems={selectedLocations} setSelectedItems={setSelectedLocations} size={[0,50,1]} />
              </Control.Box>
            </Control.Tab.Content>

            <Control.Tab.Content id={Code.Tab.Tab3} isActive={activeTab === Code.Tab.Tab3}>
              <MediaUpload label={Title.Profile} imageUrl={profileUrl} handleImageChange={handleProfileImageChange} handleAttachImage={handleAttachProfileImage} handleDeleteImage={handleDeleteProfileImage} summary={Info.ProfileSize} />
              <MediaUpload label={Title.Signature} imageUrl={signatureUrl} handleImageChange={handleSignatureImageChange} handleAttachImage={handleAttachSignatureImage} handleDeleteImage={handleDeleteSignatureImage} summary={Info.SignSize} />
            </Control.Tab.Content>

            <Control.Tab.Content id={Code.Tab.Tab4} isActive={activeTab === Code.Tab.Tab4}>
              <SingleSelectList allFormList={menuList} selectedItem={selectedMenu} setSelectedItem={setSelectedMenu} listFilter={menuFilter} setListFilter={setMenuFilter} />
            </Control.Tab.Content>
            
          </Control.Form.Data>
        </form>
      )}
      {showMenu && (<div ref={contextMenuRef}><Control.FormContextMenu keyId={keyId} activeId={activeId} showMenu={showMenu} formName={formName}  handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)} setShowMenu={setShowMenu} Privilege={Privilege} pos={menuPosition} /></div>)}
      <Actions.Form />
    </Control.Form.Frame>
  );
};

export default UserForm;